<template>
  <div class="daily-log-container">

    <div class="content-wrapper">
      <div class="calendar-section">
        <div class="calendar-header">
          <div class="current-date">{{ currentDateStr }}</div>
          <div class="calendar-toggle" @click="toggleCalendarView">
            <van-icon :name="isExpanded ? 'arrow-up' : 'arrow-down'"/>
          </div>
        </div>

        <div class="calendar-weekdays">
          <div v-for="day in weekdays" :key="day" class="weekday">{{ day }}</div>
        </div>

        <div :class="{ 'expanded': isExpanded }" class="calendar-body">
          <div
              ref="calendarScroll"
              class="calendar-scroll"
              @touchend="handleTouchEnd"
              @touchmove="handleTouchMove"
              @touchstart="handleTouchStart"
          >
            <div class="calendar-days">
              <div
                  v-for="(date, index) in visibleDates"
                  :key="index"
                  :class="{
                  'selected': isSelected(date),
                  'today': isToday(date),
                  'has-log': hasLog(date),
                  'other-month': isOtherMonth(date)
                }"
                  class="calendar-day"
                  @click="selectDate(date)"
              >
                <div class="day-number">{{ date.date() }}</div>
                <div v-if="getLogStatus(date)" :class="'log-dot status-' + getLogStatus(date)" class="log-dot"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="log-list-section">
        <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
          <van-list
              v-model="loading"
              :finished="finished"
              :finished-text="logList.length > 0 ? '没有更多了' : ''"
              :error.sync="error"
              error-text="请求失败，点击重新加载"
              @load="onLoad"
          >
            <div v-if="logList.length === 0 && !loading" class="empty-state">
              <van-empty description="暂无记录"/>
            </div>
            <div
                v-for="item in logList"
                :key="item.id"
                class="log-item"
                @click="handleLogClick(item)"
            >
              <div class="log-header">
                <span class="log-date">{{ item.workDate }}</span>
                <span :class="'log-status status-' + item.flowStatus">{{ statusMap[item.flowStatus] }}</span>
              </div>
              <div class="log-content">
                <div class="log-desc">{{ item.workContent }}</div>
              </div>
            </div>
          </van-list>
        </van-pull-refresh>
      </div>
    </div>

    <div v-if="showActionBar" class="action-bar">
      <van-button
          icon="edit"
          round
          type="info"
          class="write-log-btn"
          @click="goToAdd"
      >
        写日志
      </van-button>
    </div>

    <van-tabbar v-model="activeTab" active-color="#1989fa" inactive-color="#7d7e80">
      <van-tabbar-item name="work">
        <span style="font-size: 18px">工作日记</span>
      </van-tabbar-item>
      <van-tabbar-item name="project">
        <span style="font-size: 18px">工程日志</span>
      </van-tabbar-item>
    </van-tabbar>
  </div>
</template>

<script>
import moment from 'moment'
import DailyLogService from '@/api/dailyLog/DailyLogService.js'
import DingtalkService from '@/api/dingtalk/DingtalkService.js'
import Vue from "vue";
import * as dd from 'dingtalk-jsapi';

export default {
  name: 'DailyLog',
  data() {
    return {
      corpId: 'ding8dc103866cff029924f2f5cc6abecb85',
      activeTab: 'work',
      currentDate: moment(),
      selectedDate: moment(),
      isExpanded: false,
      logList: [],
      loading: false,
      finished: false,
      refreshing: false,
      isLoading: false,
      error: false,
      page: 1,
      pageSize: 10,
      logDates: [],
      weekdays: ['日', '一', '二', '三', '四', '五', '六'],
      statusMap: {
        '1': '未审核',
        '2': '审核中',
        '3': '已审核'
      },
      flowList: [],
      touchStartX: 0,
      touchStartY: 0,
      touchStartTime: 0,
      showActionBar: true
    }
  },
  computed: {
    currentDateStr() {
      return this.currentDate.format('YYYY 年 MM 月')
    },
    visibleDates() {
      if (this.isExpanded) {
        return this.getMonthDates()
      } else {
        return this.getWeekDates()
      }
    }
  },
  watch: {
    activeTab(val) {
      this.onRefresh()
      this.initLogDates()
      // 根据 tabbar 调整标题
      const title = val === 'work' ? '工作日记' : '工程日志'
      this.setNavigationTitle(title)
    },
    currentDate(newVal, oldVal) {
      const newMonth = newVal.format('YYYY-MM')
      const oldMonth = oldVal.format('YYYY-MM')

      if (newMonth !== oldMonth) {
        this.initLogDates()
      }
    }
  },
  created() {
    this.checkLoginAndExecute()
  },
  methods: {
    setNavigationTitle(title) {
      dd.ready(() => {
        dd.biz.navigation.setTitle({
          title: title
        })
      })
    },
    checkLoginAndExecute() {
      const token = Vue.cookie.get('token')
      const userId = Vue.cookie.get('userId')

      if (!token || !userId) {
        // 未登录，先刷新页面再执行登录
        this.reloadDingtalkEnvironment()
      } else {
        // 已登录，正常加载数据
        // 设置初始标题
        this.setNavigationTitle(this.activeTab === 'work' ? '工作日记' : '工程日志')
        this.initLogDates()
        this.getFlowList()
      }
    },
    reloadDingtalkEnvironment() {
      const reloadKey = 'dingtalk_login_reloaded_' + this.corpId
      const hasReloaded = localStorage.getItem(reloadKey)

      if (hasReloaded) {
        localStorage.removeItem(reloadKey)
        this.executeLogin()
        return
      }

      this.loading = true
      this.finished = true
      localStorage.setItem(reloadKey, 'true')
      window.location.reload()
    },
    executeLogin() {
      // 阻止列表自动加载
      this.loading = true
      this.finished = true

      this.$toast.loading({
        message: '登录中...',
        forbidClick: true,
        duration: 0
      })

      Vue.cookie.delete('token')
      Vue.cookie.delete('pm-token')
      Vue.cookie.delete('date')
      Vue.cookie.delete('name')
      Vue.cookie.delete('userId')
      Vue.cookie.delete('sjplsp')
      localStorage.removeItem('current')
      localStorage.removeItem('scrollTopPosition')
      localStorage.removeItem('size')
      localStorage.removeItem('SponsorCurrent')
      localStorage.removeItem('scrollSponsorPosition')
      localStorage.removeItem('SponsorSize')
      localStorage.removeItem('DoneCurrent')
      localStorage.removeItem('scrollDonePosition')
      localStorage.removeItem('DoneSize')
      localStorage.removeItem('tabbarName')
      localStorage.removeItem('category')
      localStorage.removeItem('businessTable')
      localStorage.removeItem('businessName')

      let loginSuccess = false

      const loginTimeout = setTimeout(() => {
        if (!loginSuccess) {
          this.$toast.fail('登录超时，请手动刷新页面重试')
          // 登录失败后恢复列表加载状态，允许用户点击重新加载
          this.loading = false
          this.finished = false
        }
      }, 10000)

      const doLogin = () => {
        dd.runtime.permission.requestAuthCode({
          corpId: this.corpId,
          onSuccess: (result) => {
            new DingtalkService().dingtalkLogin(result.code, this.corpId).then(({data}) => {
              if(data.code === 200) {
                Vue.cookie.set('token', data.token)
                Vue.cookie.set('date', data.date)
                Vue.cookie.set('name', data.name)
                Vue.cookie.set('userId', data.userId)
                Vue.cookie.set('sjplsp', data.sjplsp)
                loginSuccess = true
                clearTimeout(loginTimeout)
                this.$toast.clear()
                // 登录成功后加载数据
                this.initLogDates()
                this.getFlowList()
                // 恢复列表加载状态
                this.loading = false
                this.finished = false
                // 触发列表加载
                this.onLoad()
              } else {
                clearTimeout(loginTimeout)
                this.$toast.fail(data.message)
                this.loading = false
                this.finished = false
              }
            }).catch(err => {
              clearTimeout(loginTimeout)
              this.$toast.fail('登录失败，请手动刷新页面重试')
              this.loading = false
              this.finished = false
            })
          },
          onFail: (err) => {
            clearTimeout(loginTimeout)
            this.$toast.fail('获取授权码失败，请手动刷新页面重试')
            this.loading = false
            this.finished = false
          }
        })
      }

      if (dd.version) {
        doLogin()
      } else {
        dd.ready(() => {
          doLogin()
        })
        dd.error((err) => {
          clearTimeout(loginTimeout)
          this.$toast.fail('钉钉环境初始化失败，请手动刷新页面重试')
          this.loading = false
          this.finished = false
        })
      }
    },
    initLogDates() {
      const userId = Vue.cookie.get('userId')
      const erpMonth = this.currentDate.format('YYYY-MM')
      const dailyType = this.activeTab === 'work' ? '1' : '2'

      new DailyLogService().getLogDates(userId, erpMonth, dailyType).then(res => {
        this.logDates = res.data || []
      }).catch(err => {
        this.logDates = []
      })
    },
    getFlowList() {
      const permission = this.activeTab === 'work' ? 'cms:work:daily:01:flow' : 'cms:work:daily:02:flow'

      new DailyLogService().queryByProjectAndPermission('', permission).then(res => {
        this.flowList = res.data || []
      }).catch(err => {
        this.flowList = []
      })
    },
    getWeekDates() {
      const startOfWeek = this.currentDate.clone().startOf('week')
      const dates = []
      for (let i = 0; i < 7; i++) {
        dates.push(startOfWeek.clone().add(i, 'days'))
      }
      return dates
    },
    getMonthDates() {
      const startOfMonth = this.currentDate.clone().startOf('month').startOf('week')
      const endOfMonth = this.currentDate.clone().endOf('month').endOf('week')
      const dates = []
      let current = startOfMonth.clone()
      while (current.isBefore(endOfMonth) || current.isSame(endOfMonth, 'day')) {
        dates.push(current.clone())
        current.add(1, 'day')
      }
      return dates
    },
    toggleCalendarView() {
      this.isExpanded = !this.isExpanded
    },
    isSelected(date) {
      return this.selectedDate.isSame(date, 'day')
    },
    isToday(date) {
      return moment().isSame(date, 'day')
    },
    hasLog(date) {
      const dateStr = date.format('YYYY-MM-DD')
      return this.logDates.some(item => item.workDate === dateStr)
    },
    getLogStatus(date) {
      const dateStr = date.format('YYYY-MM-DD')
      const log = this.logDates.find(item => item.workDate === dateStr)
      if (log) {
        return log.flowStatus
      }
      return null
    },
    isOtherMonth(date) {
      return !date.isSame(this.currentDate, 'month')
    },
    selectDate(date) {
      this.selectedDate = date.clone()
      if (!date.isSame(this.currentDate, 'month')) {
        this.currentDate = date.clone()
      }
      // const dateStr = date.format('YYYY-MM-DD')
      // this.$toast(`选择了日期: ${dateStr}`)
    },
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX
      this.touchStartY = e.touches[0].clientY
      this.touchStartTime = Date.now()
    },
    handleTouchMove(e) {
    },
    handleTouchEnd(e) {
      const touchEndX = e.changedTouches[0].clientX
      const touchEndY = e.changedTouches[0].clientY
      const diffX = touchEndX - this.touchStartX
      const diffY = touchEndY - this.touchStartY
      const touchDuration = Date.now() - this.touchStartTime

      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 30 && touchDuration < 300) {
        if (diffX > 0) {
          if (this.isExpanded) {
            this.previousMonth()
          } else {
            this.previousWeek()
          }
        } else {
          if (this.isExpanded) {
            this.nextMonth()
          } else {
            this.nextWeek()
          }
        }
      }
    },
    previousWeek() {
      this.currentDate = this.currentDate.clone().subtract(1, 'week')
    },
    nextWeek() {
      this.currentDate = this.currentDate.clone().add(1, 'week')
    },
    previousMonth() {
      this.currentDate = this.currentDate.clone().subtract(1, 'month')
    },
    nextMonth() {
      this.currentDate = this.currentDate.clone().add(1, 'month')
    },
    onLoad() {
      if (this.isLoading) return

      this.isLoading = true
      this.error = false

      const params = {
        current: this.page,
        size: this.pageSize,
        dailyType: this.activeTab === 'work' ? '1' : '2'
      }

      new DailyLogService().list(params).then(res => {
        const newData = res.data.records || []

        if (this.refreshing) {
          this.logList = newData
        } else {
          this.logList = [...this.logList, ...newData]
        }

        this.refreshing = false
        this.loading = false
        this.page++

        if (newData.length < this.pageSize) {
          this.finished = true
        }

        this.isLoading = false
      }).catch(err => {
        this.$toast.fail('获取数据失败')
        this.loading = false
        this.refreshing = false
        this.finished = true
        this.error = true
        this.isLoading = false
      })
    },
    onRefresh() {
      this.logList = []
      this.page = 1
      this.finished = false
      this.error = false
      this.refreshing = true
      this.onLoad()
    },
    generateMockData() {
      const data = []
      const statuses = ['1', '2', '3']
      const tabType = this.activeTab === 'work' ? '工作' : '工程'

      for (let i = 0; i < 5; i++) {
        const date = moment().subtract(Math.floor(Math.random() * 30), 'days').format('YYYY-MM-DD')
        data.push({
          id: Date.now() + i,
          date: date,
          description: `完成了${tabType}相关的任务，包括需求分析、设计评审和开发工作，确保项目按计划推进。`,
          status: statuses[Math.floor(Math.random() * statuses.length)]
        })
      }
      return data
    },
    handleLogClick(item) {
      this.$router.push({
        path: '/dailyLog/detail',
        query: {id: item.id}
      })
    },
    goToAdd() {
      const dateStr = this.selectedDate.format('YYYY-MM-DD')
      const logType = this.activeTab === 'work' ? '1' : '2'
      const weekday = this.getWeekdayStr(this.selectedDate)
      this.$router.push({
        path: '/dailyLog/edit',
        query: {
          date: dateStr,
          weekday: weekday,
          type: logType
        }
      })
    },
    getWeekdayStr(date) {
      const weekdays = ['日', '一', '二', '三', '四', '五', '六']
      const day = date.day()
      return `星期${weekdays[day]}`
    },
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;500;600;700&display=swap');

.daily-log-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fafbfc;
  font-family: 'Source Sans 3', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.content-wrapper {
  flex: 1;
  overflow-y: auto;
  padding-bottom: calc(110px + env(safe-area-inset-bottom));
}

.calendar-section {
  background: #ffffff;
  padding: 16px 0 16px;
  margin-bottom: 12px;
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 99;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  margin-bottom: 16px;
}

.current-date {
  font-size: 20px;
  font-weight: 600;
  color: #1a2332;
  letter-spacing: 0.5px;
}

.calendar-toggle {
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  color: #0089FF;
}

.calendar-toggle:active {
  background: rgba(0, 137, 255, 0.1);
  transform: scale(0.95);
}

.calendar-weekdays {
  display: flex;
  padding: 0 12px;
  margin-bottom: 12px;
}

.weekday {
  flex: 1;
  text-align: center;
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.calendar-body {
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.calendar-body.expanded {
  max-height: 500px;
}

.calendar-scroll {
  overflow: hidden;
  touch-action: none;
}

.calendar-days {
  display: flex;
  flex-wrap: wrap;
  padding: 0 12px;
}

.calendar-day {
  width: calc(100% / 7);
  padding: 8px 0;
  text-align: center;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
}

.calendar-day:active {
  background: rgba(0, 137, 255, 0.05);
}

.calendar-day.selected .day-number {
  background: linear-gradient(135deg, #0089FF 0%, #0070E0 100%);
  color: #fff;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  line-height: 32px;
  display: inline-block;
  box-shadow: 0 4px 12px rgba(0, 137, 255, 0.3);
  font-weight: 600;
}

.calendar-day.today .day-number {
  color: #0089FF;
  font-weight: 600;
}

.calendar-day.today.selected .day-number {
  color: #fff;
}

.log-dot {
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #16a34a;
}

.log-dot.status-1 {
  background: #ea580c;
}

.log-dot.status-2 {
  background: #0089FF;
}

.log-dot.status-3 {
  background: #16a34a;
}

.calendar-day.other-month .day-number {
  color: #cbd5e1;
}

.day-number {
  font-size: 15px;
  color: #1a2332;
  position: relative;
  display: inline-block;
  width: 32px;
  height: 32px;
  line-height: 32px;
  text-align: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.log-list-section {
  padding: 4px 16px 0;
}

.log-item:first-child {
  margin-top: 8px;
}

.log-item {
  background: #ffffff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.04);
  cursor: pointer;
  position: relative;
}

.log-item:active {
  transform: scale(0.98);
  box-shadow: 0 2px 4px rgba(0, 137, 255, 0.08);
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.log-date {
  font-size: 18px;
  color: #1a2332;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.log-status {
  font-size: 14px;
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.log-status.status-1 {
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  color: #ea580c;
  border: 1px solid rgba(234, 88, 12, 0.2);
}

.log-status.status-2 {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #0089FF;
  border: 1px solid rgba(0, 137, 255, 0.2);
}

.log-status.status-3 {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #16a34a;
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.log-content .log-title {
  font-size: 17px;
  color: #1a2332;
  margin-bottom: 6px;
  font-weight: 600;
}

.log-content .log-desc {
  font-size: 16px;
  color: #64748b;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.empty-state {
  padding: 60px 0;
}

.action-bar {
  background: #ffffff;
  padding: 14px 20px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  position: fixed;
  bottom: calc(50px + env(safe-area-inset-bottom));
  left: 0;
  right: 0;
  z-index: 98;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.action-bar .van-button {
  flex: 1;
  height: 46px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 12px;
  transition: all 0.25s ease;
}

.action-bar .write-log-btn {
  flex: none;
  width: 150px;
}

.action-bar .van-button--primary {
  background: linear-gradient(135deg, #22C55E 0%, #16a34a 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
}

.action-bar .van-button--primary:active {
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.4);
  transform: scale(0.98);
}

.action-bar .van-button--info {
  background: linear-gradient(135deg, #0089FF 0%, #0070E0 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(0, 137, 255, 0.3);
}

.action-bar .van-button--info:active {
  box-shadow: 0 2px 8px rgba(0, 137, 255, 0.4);
  transform: scale(0.98);
}

.action-bar .van-button--default {
  background: #ffffff;
  border: 1px solid rgba(0, 137, 255, 0.2);
  color: #1a2332;
}

.action-bar .van-button--default:active {
  background: #f0f9ff;
  border-color: rgba(0, 137, 255, 0.3);
  transform: scale(0.98);
}

:deep(.van-tabbar) {
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
  padding-bottom: env(safe-area-inset-bottom);
}

:deep(.van-tabbar--fixed) {
  bottom: 0;
}

:deep(.van-tabbar-item--active) {
  color: #0089FF;
  font-weight: 600;
}

:deep(.van-empty__description) {
  color: #64748b;
  font-size: 15px;
}

:deep(.van-pull-refresh__head) {
  color: #0089FF;
}

:deep(.van-list__finished-text) {
  color: #94a3b8;
  font-size: 13px;
}
</style>
